import { createSlice, createAction } from "@reduxjs/toolkit";
import { RootState } from "store/store";
import {
	Post,
	PostQueryParams,
	Comment,
	CommentQueryParams,
	UserQuery,
	UserCommunity,
} from "../types";
import { Staff } from "features/staff/types";

interface PostState {
	staffs: Array<Staff>;
	posts: Array<Post>;
	comments?: Array<Comment>;
	postsTotal: number;
	postCommentsTotal?: number;
	post?: Post;
	users?: Array<UserCommunity>;
}

const initialState: PostState = {
	staffs: [],
	posts: [],
	postsTotal: 0,
	comments: [],
};

export const postSlice = createSlice({
	name: "post",
	initialState,
	reducers: {
		setStaffs: (state, action) => {
			state.staffs = action.payload;
		},
		setPosts: (state, action) => {
			state.posts = action.payload.data;
			state.postsTotal = action.payload.total;
		},
		setCommunityUsers: (state, action) => {
			state.users = action.payload.data;
		},
		setPost: (state, action) => {
			state.post = action.payload.data;
		},
		setPostComments: (state, action) => {
			state.comments = action.payload.data;
			state.postCommentsTotal = action.payload.total;
		},
		changeStatusPost: (state, action) => {
			const { id, status } = action.payload;
			const index = state.posts.findIndex(el => el._id === id);
			state.posts[index].status = status;
		},
	},
});

export const selectStaffs = (state: RootState) => state.post.staffs;
export const selectPosts = (state: RootState) => state.post.posts;
export const selectPostComments = (state: RootState) => state.post.comments;
export const selectPostsTotal = (state: RootState) => state.post.postsTotal;
export const selectPostCommentsTotal = (state: RootState) =>
	state.post.postCommentsTotal;
export const selectPost = (state: RootState) => state.post.post;
export const selectCommunityUsers = (state: RootState) => state.post.users;

export const {
	setStaffs,
	setPosts,
	setPost,
	setPostComments,
	changeStatusPost,
	setCommunityUsers,
} = postSlice.actions;

export interface GetPostsAsyncPayload {
	query: PostQueryParams;
	callback?: (posts?: Post[]) => void;
}

export interface GetCommentsAsyncPayload {
	postId: string;
	query: CommentQueryParams;
	callback?: (comments: Comment[]) => void;
}

export interface GetPostDetailsPayload {
	id: string;
	callback: (post?: Post) => void;
}

export interface CreatePostPayload {
	post?: Post;
	callback?: () => void;
}

export interface UpdatePostPayload {
	id?: string;
	post?: Post;
	willUpdate?: boolean;
	callback?: (post: Post) => void;
}

export interface UpdateCommentPayload {
	id?: string;
	status?: string;
	callback?: () => void;
	errorCallback?: () => void;
}

export interface GetCommunityUsersAsyncPayload {
	users: UserQuery[];
	callback?: (users?: Array<UserCommunity>) => void;
}

export const getPostsAsync = createAction(
	"post/getPosts",
	(payload: GetPostsAsyncPayload) => ({
		payload: payload || {},
	}),
);

export const getCommentsAsync = createAction(
	"post/getComments",
	(payload: GetCommentsAsyncPayload) => ({
		payload: payload || {},
	}),
);

export const getCommunityUsersAsync = createAction(
	"post/getUsers",
	(payload: GetCommunityUsersAsyncPayload) => ({
		payload: payload || {},
	}),
);

export const getPostDetailsAsync = createAction(
	"post/getPostDetails",
	(payload: GetPostDetailsPayload) => ({
		payload: payload,
	}),
);

export const createPostAsync = createAction(
	"post/createPost",
	(payload: CreatePostPayload) => ({
		payload: payload,
	}),
);

export const updatePostAsync = createAction(
	"post/updatePost",
	(payload: UpdatePostPayload) => ({
		payload: payload,
	}),
);

export const updateCommentAsync = createAction(
	"post/updateComment",
	(payload: UpdateCommentPayload) => ({
		payload: payload,
	}),
);

export default postSlice.reducer;
